import React, { useEffect, useState } from "react";
import Button from "view/components/Button";
import TextInput from "view/components/InputField";
import MultiSelect from "view/components/Multiselect";
import { FormikValues } from "formik";
import "./FiltersForm.css";

interface FiltersFormProps {
  values: FormikValues;
  setFieldValue: (field: string, value: any, shouldValidate?: boolean) => void;
  handleBlur: (e: React.FocusEvent<any>) => void;
  dropdownData: any;
  isLoading: boolean;
  error: any;
  touched: any;
  isSubmitting: any;
  queryResults: any;
  fetchChatDropdownData: (entity: string) => Promise<void>;
  resetForm: () => void;
}

const capitalize = (text: string) =>
  text.charAt(0).toUpperCase() + text.slice(1);

enum CategoryEnum {
  BIOLOGICAL = "Biological",
  SOCIAL = "Social",
  ECONOMIC = "Economic",
  EDUCATIONAL = "Educational",
  OUTREACH = "Outreach",
}
enum StatusEnum {
  ONGOING = "Ongoing",
  COMPLETE = "Complete",
  RETIRED = "Retired",
  NOT_RELEASED = "Not Released",
  PUBLICLY = "Publicly",
}
const enumToDropdownOptions = (enumObj: object) => {
  return Object.values(enumObj).map((value) => ({
    label: capitalize(value.replace(/_/g, " ")),
    value: value,
  }));
};

export const FiltersForm: React.FC<FiltersFormProps> = ({
  values,
  handleBlur,
  setFieldValue,
  dropdownData,
  isLoading,
  touched,
  error,
  isSubmitting,
  queryResults,
  fetchChatDropdownData,
  resetForm
}) => {
  const [hasFiltersApplied, setHasFiltersApplied] = useState<boolean>(false);

  useEffect(() => {
    try {
      const filtersApplied = 
        Boolean(values.creation_date_from) ||
        Boolean(values.creation_date_to) ||
        (Array.isArray(values.organization) && values.organization.length > 0) ||
        (Array.isArray(values.author) && values.author.length > 0) ||
        (Array.isArray(values.publisher) && values.publisher.length > 0) ||
        (Array.isArray(values.specie) && values.specie.length > 0) ||
        (Array.isArray(values.categories) && values.categories.length > 0) ||
        (Array.isArray(values.status) && values.status.length > 0) ||
        (Array.isArray(values.location) && values.location.length > 0);
      setHasFiltersApplied(filtersApplied);
    } catch (error) {
      console.error("Error checking filter status:", error);
      setHasFiltersApplied(false);
    }
  }, [values]);

  return (
    <div className="w-full flex flex-col query-filters-form">
      <div className="w-full grid grid-cols-1 sm:grid-cols-2 gap-2 items-end style">
        <TextInput
          type="date"
          label="Material Creation date"
          placeholder="YYYY/MM/DD"
          className={"py-[-2px] w-full"}
          name="creation_date_from"
          value={values.creation_date_from}
          onChange={(values: any) =>
            setFieldValue("creation_date_from", values)
          }
          onBlur={handleBlur}
        />
        <TextInput
          type="date"
          label=""
          placeholder="YYYY/MM/DD"
          className={"py-[-2px] w-full"}
          name="creation_date_to"
          value={values.creation_date_to}
          min={values.creation_date_from}
          onChange={(values: any) => setFieldValue("creation_date_to", values)}
          onBlur={handleBlur}
        />
      </div>
      {error.creation_date_from && (
        <p className="text-xs text-left text-accent_1Dark pb-1">
          {error.creation_date_from}
        </p>
      )}
      {error.creation_date_to && (
        <p className="text-xs text-left text-accent_1Dark pb-1">
          {error.creation_date_to}
        </p>
      )}
      <div className="w-full pt-2">
        <p className="w-full pb-1 text-sm font-medium text-left capitalize text-textMid dark:text-caption">
          Organization
        </p>
        <MultiSelect
          name="organization"
          placeholder="Select Organization"
          options={dropdownData.organization}
          onInputChange={(value: string) => {}}
          value={values.organization}
          defaultValue={{ value: "", label: "" }}
          isMulti={true}
          closeOnSelect={true}
          isLoading={isLoading}
          onChange={(option: any) => {
            setFieldValue("organization", option);
          }}
          onMenuOpen={() => fetchChatDropdownData("organization")}
          className={
            "!bg-[#000] border border-lineDark dar:border-lineLight rounded-md capitalize"
          }
        />
      </div>
      <div className="w-full pt-2">
        <p className="w-full pb-1 text-sm font-medium text-left capitalize text-textMid dark:text-caption">
          Author
        </p>
        <MultiSelect
          name="author"
          placeholder="Select Author"
          options={dropdownData.author}
          onInputChange={(value: string) => {}}
          className={
            "border border-lineDark dark:border-lineLight rounded-md capitalize"
          }
          defaultValue={{ value: "", label: "" }}
          isMulti={true}
          closeOnSelect={true}
          value={values.author}
          onChange={(option: any) => {
            setFieldValue("author", option);
          }}
          onMenuOpen={() => fetchChatDropdownData("author")}
        />
      </div>
      <div className="w-full pt-2">
        <p className="w-full pb-1 text-sm font-medium text-left capitalize text-textMid dark:text-caption">
          Publisher
        </p>
        <MultiSelect
          name="publisher"
          placeholder="Select Publishers"
          options={dropdownData.publisher}
          onInputChange={(value: string) => {}}
          className={
            "border border-lineDark dark:border-lineLight rounded-md capitalize"
          }
          defaultValue={{ value: "", label: "" }}
          isMulti={true}
          closeOnSelect={true}
          value={values.publisher}
          onChange={(option: any) => {
            setFieldValue("publisher", option);
          }}
          onMenuOpen={() => fetchChatDropdownData("publisher")}
        />
      </div>
      <div className="w-full pt-2">
        <p className="w-full pb-1 text-sm font-medium text-left capitalize text-textMid dark:text-caption">
          Species
        </p>
        <MultiSelect
          name="specie"
          placeholder="Select Species"
          options={dropdownData.specie}
          onInputChange={(value: string) => {}}
          className={
            "border border-lineDark dark:border-lineLight rounded-md capitalize"
          }
          defaultValue={{ value: "", label: "" }}
          isMulti={true}
          closeOnSelect={true}
          value={values.specie}
          onChange={(option: any) => {
            setFieldValue("specie", option);
          }}
          onMenuOpen={() => fetchChatDropdownData("specie")}
        />
      </div>
      <div className="w-full pt-2">
        <p className="w-full pb-1 text-sm font-medium text-left capitalize text-textMid dark:text-caption">
          Categories
        </p>
        <MultiSelect
          name="categories"
          placeholder="Select Categories"
          options={enumToDropdownOptions(CategoryEnum)}
          onInputChange={(value: string) => {}}
          className={
            "border border-lineDark dark:border-lineLight rounded-md capitalize"
          }
          defaultValue={{ value: "", label: "" }}
          isMulti={true}
          closeOnSelect={true}
          value={values.categories}
          onChange={(option: any) => {
            setFieldValue("categories", option);
          }}
        />
      </div>
      <div className="w-full pt-4">
        <p className="w-full pb-1 text-sm font-medium text-left capitalize text-textMid dark:text-caption">
          Status
        </p>
        <MultiSelect
          name="status"
          placeholder="Select Status"
          options={enumToDropdownOptions(StatusEnum)}
          onInputChange={(value: string) => {}}
          className={
            "border border-lineDark dark:border-lineLight rounded-md capitalize"
          }
          defaultValue={{ value: "", label: "" }}
          isMulti={true}
          closeOnSelect={true}
          value={values.status}
          onChange={(option: any) => {
            setFieldValue("status", option);
          }}
        />
      </div>
      <div className="w-full pt-4">
        <p className="w-full pb-1 text-sm font-medium text-left capitalize text-textMid dark:text-caption">
          Location
        </p>
        <MultiSelect
          name="location"
          placeholder="Select Location"
          options={dropdownData.location}
          onInputChange={(value: string) => {}}
          className={
            "border border-lineDark dark:border-lineLight rounded-md capitalize"
          }
          defaultValue={{ value: "", label: "" }}
          isMulti={true}
          closeOnSelect={true}
          value={values.location}
          onChange={(option: any) => {
            setFieldValue("location", option);
          }}
          onMenuOpen={() => fetchChatDropdownData("location")}
        />
      </div>

      <div className="flex justify-end gap-2 pb-4 pt-4">
        {hasFiltersApplied && (
          <Button
              type="reset"
              text="Clear Filters"
              variant="outlined"
              filledColor="primary"
              outlinedColor="primary"
              textColor="textWhite"
              className="py-2"
              height="13"
              fontStyle="font-semibold"
              disabled={isSubmitting}
              onClick={resetForm}
            />
        )}
        <Button
          type="submit"
          text="Apply"
          filledColor="primary"
          outlinedColor="primary"
          textColor="textWhite"
          className="w-24 py-2"
          width="35"
          height="13"
          fontStyle="font-semibold"
          variant="filled"
          disabled={
            isSubmitting ||
            queryResults?.length === 0 ||
            (values.creation_date_from === "" &&
              values.creation_date_to === "" &&
              values.organization?.length === 0 &&
              values.author?.length === 0 &&
              values.publisher?.length === 0 &&
              values.specie?.length === 0 &&
              values.categories?.length === 0 &&
              values.status?.length === 0 &&
              values.location?.length === 0)
          }
        />
      </div>
    </div>
  );
};
